/**
 * Partner-felvitel: tranzakció-vezérelt mező-mátrix (61Y / partner-L1).
 *
 * A pultos először azt választja ki, MIT INTÉZ az ügyfél (vásárol / bérel /
 * szervizeltet), és a felvitel onnantól csak azt kéri, ami az adott ügyhöz kell.
 * A mátrix SZÁNDÉKOSAN adat (config), nem elágazás-erdő a komponensben — így a
 * szabály egy helyen olvasható, tesztelhető, és a kliens-ratify után egyetlen
 * táblázatban módosítható.
 *
 * Forrás: Zsuzsi-csomag (2026-07-27 relay, 8 üzenet) + a 2026-07-28-i mockup-ratify.
 * Kulcs-döntések, amiket a mátrix hordoz:
 *   - bérlésnél MINDIG kötelező a személyi (nincs kivétel)
 *   - a személyi-kivétel csak szervizre vonatkozik: ott CSAK céges költségviselő
 *     vagy kedvezmény esetén kell (meghatalmazott-ellenőrzés miatt)
 *   - a bérlő maga MAGÁNSZEMÉLY (ő a szerződő); a cég költségviselőként és a
 *     meghatalmazotti listán jelenik meg
 *   - soft-enforce: a hiányzó kötelező mező JELÖLVE van, de nem blokkolja a mentést
 */

/** Amit az ügyfél éppen intéz — ez vezérli a kötelezőséget. */
export type TransactionIntent = 'PURCHASE' | 'RENTAL' | 'SERVICE';

/** A mátrix sorai — egy sor több űrlap-mezőt is takarhat (pl. a lakcím hármat). */
export type PartnerFieldKey =
  | 'name'
  | 'phone'
  | 'email'
  | 'address'
  | 'idCard'
  | 'addressCard'
  | 'birthData'
  | 'mothersName'
  | 'taxNumber'
  | 'representative';

/**
 * Egy mező kötelezőségi állapota az adott ügytípusban.
 *  - `required`   — kötelező (hiánya piros jelölést kap)
 *  - `conditional`— csak bizonyos feltétel mellett kötelező (lásd `condition`)
 *  - `optional`   — kitölthető, de nem várjuk el
 */
export type FieldRequirement = 'required' | 'conditional' | 'optional';

/** A feltételes kötelezőség kiváltó oka — a UI ebből képez magyarázó szöveget. */
export type RequirementCondition = 'CORPORATE_PAYER' | 'DISCOUNT' | 'COMPANY_INVOICE';

export interface FieldRule {
  requirement: FieldRequirement;
  /**
   * Csak `conditional`-nál értelmezett. Több feltétel = VAGY-kapcsolat.
   *
   * ⚠️ A feltétel a MEZŐHÖZ tartozik, nem a tokenhez: ugyanaz a token más mezőnél
   * más kísérő-feltételekkel állhat. (Ezt a szétválasztást a #1008 [CR] F1 kérte —
   * korábban a `CORPORATE_PAYER` ág maga OR-ozta be a kedvezményt, ezért a
   * `representative` is tüzelt kedvezménynél, ami magánszemélytől kért képviselőt.)
   */
  condition?: RequirementCondition | RequirementCondition[];
}

/** A partner-felvitel aktuális kontextusa — ebből dől el a feltételes kötelezőség. */
export interface PartnerFormContext {
  intent: TransactionIntent;
  /** A költségviselő cég (nem a szerződő!) — szerviznél ez hozza be a személyit. */
  hasCorporatePayer?: boolean;
  /** Kedvezmény jön szóba → meghatalmazott-ellenőrzés kell. */
  hasDiscount?: boolean;
  /** Céges (adószámos) számlát kér. */
  wantsCompanyInvoice?: boolean;
}

const R: FieldRule = { requirement: 'required' };
const O: FieldRule = { requirement: 'optional' };
/** Egy vagy több feltétel; többnél VAGY-kapcsolat. */
const cond = (...conditions: RequirementCondition[]): FieldRule => ({
  requirement: 'conditional',
  condition: conditions.length === 1 ? conditions[0]! : conditions,
});

/**
 * A mátrix maga. Sorok = mezők, oszlopok = ügytípusok.
 *
 * ⚠️ Ez a tábla a kliens-ratify tárgya — módosítás CSAK ratifikált döntés után.
 */
export const PARTNER_FIELD_MATRIX: Record<PartnerFieldKey, Record<TransactionIntent, FieldRule>> = {
  name: { PURCHASE: R, RENTAL: R, SERVICE: R },
  phone: { PURCHASE: O, RENTAL: R, SERVICE: R },
  email: { PURCHASE: O, RENTAL: R, SERVICE: R },
  address: { PURCHASE: O, RENTAL: R, SERVICE: R },
  // Bérlésnél nincs kivétel; szerviznél csak céges költségviselő VAGY kedvezmény esetén
  // (mindkettőnél meghatalmazottat kell ellenőrizni) — a két feltétel itt van kimondva,
  // NEM a token feloldásában, különben a többi CORPORATE_PAYER-es cellára is átszivárogna.
  idCard: { PURCHASE: O, RENTAL: R, SERVICE: cond('CORPORATE_PAYER', 'DISCOUNT') },
  addressCard: { PURCHASE: O, RENTAL: R, SERVICE: O },
  birthData: { PURCHASE: O, RENTAL: R, SERVICE: O },
  mothersName: { PURCHASE: O, RENTAL: R, SERVICE: O },
  taxNumber: {
    PURCHASE: cond('COMPANY_INVOICE'),
    RENTAL: cond('COMPANY_INVOICE'),
    SERVICE: cond('COMPANY_INVOICE'),
  },
  // A bérlő a szerződő magánszemély; a képviselő/átvevő a céges költségviselőnél kell.
  representative: {
    PURCHASE: O,
    RENTAL: cond('CORPORATE_PAYER'),
    SERVICE: cond('CORPORATE_PAYER'),
  },
};

/**
 * Egy mező TÉNYLEGES kötelezősége az adott kontextusban — a feltételes szabályokat
 * is feloldva. Ez az egyetlen belépési pont a UI felé.
 */
export function isFieldRequired(field: PartnerFieldKey, ctx: PartnerFormContext): boolean {
  return requirementReason(field, ctx) !== null;
}

/** Egyetlen feltétel teljesül-e — token szerint, kísérő-feltételek NÉLKÜL. */
function conditionHolds(condition: RequirementCondition, ctx: PartnerFormContext): boolean {
  switch (condition) {
    case 'CORPORATE_PAYER':
      return Boolean(ctx.hasCorporatePayer);
    case 'DISCOUNT':
      return Boolean(ctx.hasDiscount);
    case 'COMPANY_INVOICE':
      return Boolean(ctx.wantsCompanyInvoice);
    default:
      return false;
  }
}

/**
 * MIÉRT kötelező a mező ebben a kontextusban — a UI ebből képzi a magyarázó szöveget.
 *
 *  - `null`               — nem kötelező
 *  - `'ALWAYS'`           — az ügytípus miatt mindig az
 *  - `RequirementCondition` — a ténylegesen teljesült feltétel
 *
 * A ténylegesen teljesült feltételt adjuk vissza (nem az elsőt a listából), hogy a UI
 * ne magyarázzon „céges költségviselő"-t olyan ügyfélnél, akinek csak kedvezménye van.
 */
export function requirementReason(
  field: PartnerFieldKey,
  ctx: PartnerFormContext
): 'ALWAYS' | RequirementCondition | null {
  const rule = PARTNER_FIELD_MATRIX[field][ctx.intent];
  if (rule.requirement === 'required') return 'ALWAYS';
  if (rule.requirement === 'optional') return null;

  const conditions = rule.condition
    ? Array.isArray(rule.condition)
      ? rule.condition
      : [rule.condition]
    : [];

  return conditions.find(c => conditionHolds(c, ctx)) ?? null;
}

/** Az adott kontextusban kötelező mezők listája (a piros-jelöléshez és az összegzéshez). */
export function requiredFields(ctx: PartnerFormContext): PartnerFieldKey[] {
  return (Object.keys(PARTNER_FIELD_MATRIX) as PartnerFieldKey[]).filter(f =>
    isFieldRequired(f, ctx)
  );
}

/**
 * A hiányzó kötelező mezők — soft-enforce: a mentés NEM blokkolt, ezt a listát a UI
 * figyelmeztetésként mutatja, és a mezőket pirosan jelöli.
 */
export function missingRequiredFields(
  ctx: PartnerFormContext,
  filled: Partial<Record<PartnerFieldKey, boolean>>
): PartnerFieldKey[] {
  return requiredFields(ctx).filter(f => !filled[f]);
}
